import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { AxiosError } from "axios";
import type { BannerSwap, ChannelSettings } from "../../../utils/@types";
import { apiService } from "../../../api/api";
import { bannerDashboardApi } from "../../../api/bannerDashboardApi";

type RemoveResult = {
  message: string;
  removedChannelIds: string[];
  remainingChannelIds: string[];
  swapCancelled: boolean;
};

type Props = {
  swap: BannerSwap;
  /** Copy differs for scheduled vs running swaps. */
  mode: "scheduled" | "running";
  disabled?: boolean;
  toast: React.RefObject<Toast | null>;
  onChannelRemoved: (result: RemoveResult) => Promise<void>;
};

const SwapChannelRemoveSection: React.FC<Props> = ({
  swap,
  mode,
  disabled = false,
  toast,
  onChannelRemoved,
}) => {
  const [channelDetails, setChannelDetails] = useState<ChannelSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingChannelId, setRemovingChannelId] = useState<string | null>(
    null
  );
  const [confirmChannelId, setConfirmChannelId] = useState<string | null>(null);
  const [confirmTitle, setConfirmTitle] = useState("");

  useEffect(() => {
    const fetchChannelDetails = async () => {
      try {
        setLoading(true);
        const allSettings =
          await apiService.channelSettings.getAllChannelSettings();
        const idList = swap.channelIDs.map(String);
        const seen = new Set<string>();
        const details: ChannelSettings[] = [];
        for (const id of idList) {
          const row = allSettings.find(
            (c) => c.ChannelId === id || (c as { id?: string }).id === id
          );
          if (row && !seen.has(row.ChannelId)) {
            seen.add(row.ChannelId);
            details.push(row);
          }
        }
        setChannelDetails(details);
      } catch {
        setChannelDetails([]);
      } finally {
        setLoading(false);
      }
    };
    if (swap.channelIDs?.length) {
      void fetchChannelDetails();
    } else {
      setChannelDetails([]);
      setLoading(false);
    }
  }, [swap.channelIDs]);

  const requestRemove = (channelId: string, channelTitle?: string) => {
    setConfirmChannelId(channelId);
    setConfirmTitle(channelTitle || channelId);
  };

  const confirmRemove = async () => {
    if (!confirmChannelId) {
      return;
    }
    setRemovingChannelId(confirmChannelId);
    try {
      const result = await bannerDashboardApi.patchRemoveChannelsFromSwap(
        swap.swapUID,
        [confirmChannelId]
      );
      toast.current?.show({
        severity: "success",
        summary: result.swapCancelled ? "Swap cancelled" : "Channel removed",
        detail: result.message,
      });
      setConfirmChannelId(null);
      await onChannelRemoved(result);
    } catch (err) {
      const ax = err as AxiosError<{ message?: string }>;
      const msg = ax.response?.data?.message ?? "Failed to remove channel.";
      toast.current?.show({
        severity: "error",
        summary: "Remove failed",
        detail: msg,
      });
    } finally {
      setRemovingChannelId(null);
    }
  };

  if (loading) {
    return (
      <p className="text-sm text-gray-500 m-0">Loading channels…</p>
    );
  }

  if (!channelDetails.length) {
    return null;
  }

  return (
    <>
      <div className="border-t border-gray-100 pt-4">
        <h4 className="text-sm font-semibold text-gray-900 m-0 mb-2">
          Channels in this swap
        </h4>
        <ul className="list-none m-0 p-0 flex flex-col gap-2">
          {channelDetails.map((ch) => (
            <li
              key={ch.ChannelId}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2 bg-gray-50"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 m-0 truncate">
                  {ch.ChannelTitle || ch.ChannelId}
                </p>
                <p className="text-xs text-gray-500 m-0 font-mono truncate">
                  {ch.ChannelId}
                </p>
              </div>
              <Button
                label="Remove from swap"
                icon="pi pi-times"
                severity="danger"
                size="small"
                outlined
                loading={removingChannelId === ch.ChannelId}
                disabled={
                  disabled ||
                  (removingChannelId !== null &&
                    removingChannelId !== ch.ChannelId)
                }
                onClick={() => requestRemove(ch.ChannelId, ch.ChannelTitle)}
              />
            </li>
          ))}
        </ul>
      </div>

      <Dialog
        header="Remove channel from swap"
        visible={confirmChannelId !== null}
        style={{ width: "min(480px, 92vw)" }}
        onHide={() => {
          if (!removingChannelId) {
            setConfirmChannelId(null);
          }
        }}
        closable={!removingChannelId}
      >
        <p className="text-sm text-gray-700 m-0">
          {mode === "running" ? (
            <>
              Remove <strong>{confirmTitle}</strong> from this swap? Its banner
              will revert to the current default immediately.
            </>
          ) : (
            <>
              Remove <strong>{confirmTitle}</strong> from this scheduled swap?
              It will no longer receive this banner when the swap runs.
            </>
          )}
        </p>
        {swap.channelIDs.length === 1 && (
          <p className="text-sm text-amber-700 mt-3 mb-0">
            This is the last channel on the swap. Removing it will cancel the
            entire swap.
          </p>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button
            label="Cancel"
            severity="secondary"
            disabled={!!removingChannelId}
            onClick={() => setConfirmChannelId(null)}
          />
          <Button
            label="Remove from swap"
            severity="danger"
            loading={!!removingChannelId}
            onClick={() => void confirmRemove()}
          />
        </div>
      </Dialog>
    </>
  );
};

export default SwapChannelRemoveSection;
